import { useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { DrawerCompanion } from '@components/ui/Drawer/DrawerCompanion';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { useHeldRecord } from '@hooks/useHeldRecord';
import { StockDetailBody } from '../../../LogisticsPage/panels/StockDetailDrawer';
import { stockFormPlumbingFor } from '../../../LogisticsPage/orderDialogDerivations';
import { useLogisticsQueries } from '../../../LogisticsPage/useLogisticsQueries';
import { useDeleteMaterial } from '../../../LogisticsPage/useLogisticsMutations';
import { fold } from '../../../LogisticsPage/units';
import { useSchedulerPortalContainer } from '../../context/SchedulerPortalContainer';
import styles from './TaskPanelStockDetail.module.css';

interface ITaskPanelStockDetailProps {
  siteId: string;
  /** The material whose record is shown — the tapped row's own name. The
   *  LIVE level is re-looked up by fold on every payload, so a save's refetch
   *  updates the record in place. Null shows nothing. */
  materialName: string | null;
  /** Drives the companion's enter/exit animation (`DrawerCompanion`). */
  open: boolean;
  onClose: () => void;
  /** The exit animation has finished — the host unmounts this. */
  onExited: () => void;
}

/**
 * The Stock tab's stock record: the SAME record body the Logistics stock
 * drawer shows, docked beside the task drawer as a `DrawerCompanion` — exactly
 * what the site map's area drawer does with its own stock rows. Mounted at the
 * drawer's popup level (the tab body scrolls, and would clip it).
 *
 * Reads the same logistics payload the tab already waited for, so in practice
 * it is never seen loading. Delete stages into a `DiscardModal` and fires the
 * Logistics page's own delete mutation; the failure is reported inline (the
 * drawer has no toast stack). A deleted material closes the companion — the
 * held copy only carries the slide-out.
 */
export function TaskPanelStockDetail({ siteId, materialName, open, onClose, onExited }: ITaskPanelStockDetailProps) {
  const { t } = useTranslation();
  const tz = useSelector(selectSiteTz);
  const portalContainer = useSchedulerPortalContainer();
  const { logisticsQuery } = useLogisticsQueries(siteId);
  const data = logisticsQuery.data;
  const [pendingDelete, setPendingDelete] = useState<InventoryLevel | null>(null);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const deleteMaterial = useDeleteMaterial(siteId, { onError: () => setDeleteFailed(true), revision: null });

  // The LIVE record, by the same NFKC+lowercase fold every material link uses.
  const key = materialName === null ? null : fold(materialName);
  const live = !data || key === null ? null : (data.inventory.find((l) => fold(l.materialName) === key) ?? null);
  const shown = useHeldRecord(live);

  // The record is gone (a delete's refetch): close — the held copy slides
  // out. An effect, not a render-time adjust, because the open state lives
  // in the host. Read through an Effect Event so the parent's fresh closure
  // is never an effect dep.
  const closeGone = useEffectEvent(() => onClose());
  useEffect(() => {
    if (open && data && !live) closeGone();
  }, [open, data, live]);

  if (!data || !tz || !shown) return null;
  return (
    <>
      <DrawerCompanion
        open={open}
        onClose={onClose}
        onExited={onExited}
        ariaLabel={`${t('logistics.stockDetail.title')} ${shown.materialName}`}
        closeLabel={t('logistics.stockDetail.close')}
      >
        {deleteFailed ? (
          <p role="alert" className={styles.error}>
            {t('logistics.inventory.deleteError')}
          </p>
        ) : null}
        {/* Keyed per material like the stock drawer's own body — a tap on a
            DIFFERENT row must reset the tabs to Overview. */}
        <StockDetailBody
          key={shown.materialName}
          level={shown}
          tz={tz}
          onClose={onClose}
          onDelete={setPendingDelete}
          canEdit={data.canEdit}
          {...stockFormPlumbingFor(siteId, data)}
        />
      </DrawerCompanion>
      {pendingDelete ? (
        <DiscardModal
          open
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            setDeleteFailed(false);
            deleteMaterial.mutate(pendingDelete.materialName);
            setPendingDelete(null);
          }}
          title={t('logistics.inventory.confirmDelete.title', { name: pendingDelete.materialName })}
          description={t('logistics.inventory.confirmDelete.body')}
          container={portalContainer}
        />
      ) : null}
    </>
  );
}
